import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const APPRECIATION_CATEGORIES = [
  'Excellent Teaching & Pedagogy', 'High Punctuality & Dedication', 'Helpful & Supportive Colleague',
  'Outstanding Event / Class Management', 'Student Motivation & Care', 'Other Commendation'
];
const uid = (u) => String(u?._id || u?.id || '');
const fmt = (d) => { try { return new Date(d).toLocaleDateString('en-IN', { dateStyle: 'medium' }); } catch { return ''; } };
const chip = (bg, fg) => ({ display: 'inline-block', background: bg, color: fg, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, marginRight: 4, marginBottom: 4 });

/**
 * Conduct & Recognition Ledger: complaint count + positive recognition count with historical logs.
 * Props: viewer (logged-in user), teacherId (profile owner), canAppreciate (show "give appreciation" form).
 */
export default function ConductLedger({ viewer, teacherId, canAppreciate = false }) {
  const viewerId = uid(viewer);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ categories: [], message: '' });
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!teacherId) return;
    axios.get(`${API_BASE_URL}/api/staff-feedback/ledger/${teacherId}`, { headers: { 'x-user-id': viewerId } })
      .then((r) => { setData(r.data); setErr(''); })
      .catch((e) => setErr(e.response?.data?.error || 'Could not load ledger'));
  }, [teacherId, viewerId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (c) => setForm((f) => ({ ...f, categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c] }));

  const submit = async () => {
    setBusy(true); setMsg('');
    try {
      await axios.post(`${API_BASE_URL}/api/staff-feedback/appreciations`, { recipientTeacherId: teacherId, ...form }, { headers: { 'x-user-id': viewerId } });
      setForm({ categories: [], message: '' });
      setMsg('✅ Appreciation sent');
      load();
    } catch (e) { setMsg('❌ ' + (e.response?.data?.error || e.message)); }
    setBusy(false);
  };

  if (err) return <p style={{ color: '#b91c1c', fontSize: 13 }}>{err}</p>;
  if (!data) return <p style={{ color: '#64748b', fontSize: 13 }}>Loading conduct ledger…</p>;
  const { counts, complaints, appreciations } = data;
  const box = { flex: 1, minWidth: 120, borderRadius: 10, padding: 10, textAlign: 'center' };

  return (
    <div style={{ marginTop: 16, textAlign: 'left' }}>
      <h4 style={{ margin: '0 0 8px', color: '#0f172a' }}>⚖️ Conduct &amp; Recognition Ledger</h4>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ ...box, background: '#fee2e2', border: '1px solid #fca5a5' }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#b91c1c' }}>{counts.complaints}</div>
          <div style={{ fontSize: 11, color: '#7f1d1d' }}>Complaints ({counts.openComplaints} open)</div>
        </div>
        <div style={{ ...box, background: '#dcfce7', border: '1px solid #86efac' }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#15803d' }}>{counts.appreciations}</div>
          <div style={{ fontSize: 11, color: '#14532d' }}>Recognitions ({counts.principalCommendations} principal)</div>
        </div>
      </div>


      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <b style={{ fontSize: 13, color: '#b91c1c' }}>Complaints Received</b>
          {complaints.length === 0 && <p style={{ fontSize: 12, color: '#64748b' }}>No complaints on record.</p>}
          {complaints.map((c) => (
            <div key={c._id} style={{ border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 8, padding: 8, marginTop: 6 }}>
              <div>{c.categories.map((x) => <span key={x} style={chip('#fecaca', '#991b1b')}>{x}</span>)}</div>
              <div style={{ fontSize: 13 }}>{c.remarks}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                {fmt(c.incidentDate)} · {String(c.status).replace(/_/g, ' ')}{c.reporterName ? ` · by ${c.reporterName}` : ''}
              </div>
            </div>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <b style={{ fontSize: 13, color: '#15803d' }}>Appreciations &amp; Commendations</b>
          {appreciations.length === 0 && <p style={{ fontSize: 12, color: '#64748b' }}>No recognitions yet.</p>}
          {appreciations.map((a) => (
            <div key={a._id} style={{ border: '1px solid #bbf7d0', background: '#f0fdf4', borderRadius: 8, padding: 8, marginTop: 6 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{a.badge}</div>
              <div>{a.categories.map((x) => <span key={x} style={chip('#bbf7d0', '#166534')}>{x}</span>)}</div>
              <div style={{ fontSize: 13 }}>{a.message}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                {fmt(a.date)} · {a.awardedByRole === 'principal' ? 'Principal' : 'Peer'}: {a.senderName}
              </div>
            </div>
          ))}
        </div>
      </div>

      {canAppreciate && viewerId !== String(teacherId) && (
        <div style={{ marginTop: 14, borderTop: '1px solid #e2e8f0', paddingTop: 10 }}>
          <b style={{ fontSize: 13 }}>🌟 Give Appreciation</b>
          <div style={{ marginTop: 6 }}>
            {APPRECIATION_CATEGORIES.map((c) => (
              <label key={c} style={{ display: 'block', fontSize: 12 }}>
                <input type="checkbox" checked={form.categories.includes(c)} onChange={() => toggle(c)} /> {c}
              </label>
            ))}
          </div>
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3}
            placeholder="Write a short appreciation message" style={{ width: '100%', marginTop: 6, boxSizing: 'border-box' }} />
          <button disabled={busy} onClick={submit}
            style={{ marginTop: 6, padding: '8px 14px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}>
            {busy ? 'Sending…' : 'Send Appreciation'}
          </button>
          {msg && <span style={{ marginLeft: 8, fontSize: 12 }}>{msg}</span>}
        </div>
      )}
    </div>
  );
}
